import { CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui'
import type { PriceReview, PriceReviewLine } from '../api/reviewTypes'

export interface PushResultBannerProps {
  review: PriceReview
  lines: PriceReviewLine[]
  isAdmin: boolean
  retrying: boolean
  onRetry: () => void
}

function fmtDate(iso: string | null): string {
  if (!iso) return 'the effective date'
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
}

/**
 * What happened in Odoo. A failed push is per line: the lines that did write
 * stay written, and Retry redoes only the ones listed here.
 */
export function PushResultBanner({ review, lines, isAdmin, retrying, onRetry }: PushResultBannerProps) {
  if (review.status === 'pushed') {
    const written = lines.filter((l) => l.written_at).length
    return (
      <div className="flex items-center gap-2 border-b border-success-border bg-success-bg px-6 py-2 text-sm text-success-text">
        <CheckCircle2 size={16} />
        {written} {written === 1 ? 'line' : 'lines'} written to the Odoo pricelist. New prices start {fmtDate(review.effective_date)}.
      </div>
    )
  }
  if (review.status !== 'push_failed') return null
  const failed = lines.filter((l) => l.write_error && !l.written_at)
  return (
    <div className="flex flex-col gap-2 border-b border-danger bg-danger-bg px-6 py-2 text-sm text-danger-text">
      <div className="flex items-center gap-3">
        <span>
          {review.push_error ?? 'Some lines were not written to Odoo.'} Lines that did write are not repeated.
        </span>
        {isAdmin ? (
          <Button variant="secondary" size="sm" onClick={onRetry} disabled={retrying}>
            {retrying ? 'Retrying…' : 'Retry failed lines'}
          </Button>
        ) : null}
      </div>
      <ul className="m-0 flex list-disc flex-col gap-0.5 pl-5">
        {failed.map((l) => (
          <li key={l.id}>
            {l.product_name}: {l.write_error}
          </li>
        ))}
      </ul>
    </div>
  )
}
